"use client";
import { setRedirectAfterLogin } from "@/app/page/rootComponents/AuthHubListener";
import ClickableText from "@/components/styled/button/ClickableText";
import br from "@/styles/breakpoints.module.css";
import LocalKeyValueStore from "@/utils/LocalKeyValueStore";
import { themeColors } from "@/utils/themeColors";
import { cleanEmail, usernameFromEmail } from "@knowt/syncing/hooks/user/auth";
import { TEMP_CACHED_SIGN_IN_SOURCE_KEY } from "@knowt/syncing/hooks/user/types";
import { validateSignupForm } from "@knowt/syncing/hooks/user/userVerificationUtils";
import { useCombinedState } from "@knowt/syncing/utils/hooks/useCombinedState";
import { signUp } from "aws-amplify/auth";
import clsx from "clsx";
import { usePathname, useSearchParams } from "next/navigation";
import { useCallback, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { toast } from "react-hot-toast";
import { AuthEvent } from "../AuthPopup/utils/constants";
import { useAuthSlideContextSelector } from "../AuthSlide/hook/useAuthSlideContext";
import AuthKnowtLogo from "../components/AuthKnowtLogo";
import {
    AuthLeftContainer,
    AuthLeftInnerContainer,
    AuthMainContainer,
    AuthRightContainer,
} from "../components/styled/Containers";
import { AuthForm, AuthInputWithLabel, AuthSubmitButton } from "../components/styled/Forms";
import { GapPerSection, OrDivider } from "../components/styled/Separator";
import { AuthSignupSocialBtns } from "../components/styled/SocialButtons";
import { AuthDescription, AuthTitle } from "../components/styled/Typographies";
import { SS_MANUALLY_VERIFY_SIGNIN_KEY, SS_REDIRECT_KEY } from "../utils";
import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import Circular from "@/components/Circular";
import { Check, X } from "lucide-react";
import { strokeWidth } from "@/utils/iconProps";
import { useThemeContextSelector } from "@/utils/theme";
import signupAnimationDark from "@/public/images/lottie/sign-up-join-the-party-dark.json";
import signupAnimationLight from "@/public/images/lottie/sign-up-join-the-party-light.json";

const PasswordRequirementsChecklist = ({ password }: { password: string }) => {
    return (
        <FlexColumn>
            <p
                className="secondaryText1"
                style={{ color: themeColors.neutralBlack, marginBottom: "1.2rem" }}
                data-testid="auto-id-c48e13bf-160f-4349-8fee-56e4c1a28797">
                Password requirements
            </p>
            <FlexRowAlignCenter style={{ gap: "1.2rem" }}>
                {[
                    { isCheck: password.trim().length >= 8, label: "8 characters" },
                    { isCheck: /\d+/.test(password), label: "1 number" },
                ].map(({ label, isCheck }) => (
                    <FlexRowAlignCenter key={label} style={{ gap: "0.8rem" }}>
                        <Circular
                            radius="2.4rem"
                            style={{ backgroundColor: isCheck ? themeColors.successPrimary : themeColors.background }}>
                            {isCheck ? (
                                <Check strokeWidth={strokeWidth.normal} size={"1.2rem"} />
                            ) : (
                                <X strokeWidth={strokeWidth.normal} size={"1.2rem"} />
                            )}
                        </Circular>
                        <p
                            className="secondaryText2"
                            data-testid="auto-id-17225043-bfb4-4158-b2d2-073a7cba4918">{label}</p>
                    </FlexRowAlignCenter>
                ))}
            </FlexRowAlignCenter>
        </FlexColumn>
    );
};

const SignupPage = ({
    onSignUp,
    onClose = () => null,
    event = AuthEvent.GENERAL_SIGNUP,
}: {
    onSignUp: (username: string) => void;
    onClose: () => void;
    event?: AuthEvent;
}) => {
    const isDarkMode = useThemeContextSelector(state => state.isDarkMode);
    const { executeRecaptcha } = useGoogleReCaptcha();

    const pathname = usePathname();
    const searchParams = useSearchParams();

    const [authStatus, setAuthStatus] = useState<"loading" | "success" | "failure" | null>(null);

    const openAuthSlide = useAuthSlideContextSelector(state => state.openAuthSlide);

    const [profileForm, updateProfileForm] = useCombinedState(() => ({
        email: "",
        password: "",
    }));

    const { email, password } = profileForm;

    const handleReCaptchaVerify = useCallback(async () => {
        if (!executeRecaptcha) {
            toast.error("An error occurred, please try again later");
            throw new Error("Execute recaptcha not yet available");
        }

        return await executeRecaptcha("yourAction");
    }, [executeRecaptcha]);

    const signUpUser = async () => {
        const cleanedEmail = cleanEmail(email);
        const SIGNUP_TOAST_ID = "signupToastId";

        const token = await handleReCaptchaVerify();

        try {
            setRedirectAfterLogin(pathname, searchParams);
            validateSignupForm({ email: cleanedEmail, password });

            setAuthStatus("loading");

            toast.loading("Setting up your account...", { id: SIGNUP_TOAST_ID });

            const amplifyUsername = usernameFromEmail(cleanedEmail);
            await signUp({
                password,
                username: amplifyUsername,
                options: {
                    userAttributes: { email: cleanedEmail },
                    validationData: { token },
                    autoSignIn: true,
                },
            });
            await LocalKeyValueStore.setWithExpiry(TEMP_CACHED_SIGN_IN_SOURCE_KEY, event);

            window?.sessionStorage.setItem(
                SS_MANUALLY_VERIFY_SIGNIN_KEY,
                JSON.stringify({ username: amplifyUsername, password })
            );

            setAuthStatus("success");
            toast.success("Account created successfully!", { id: SIGNUP_TOAST_ID });
            onSignUp(amplifyUsername);
        } catch (signupError) {
            window.sessionStorage.removeItem(SS_REDIRECT_KEY);

            let toastMessage = "An error occurred";

            if (signupError?.message?.includes("Email already exists")) {
                toastMessage = "Sorry looks like you already have an account with this email. Try logging in instead";
            } else if (signupError?.message?.includes("a robot?")) {
                toastMessage = "Sorry, our servers think you might be a robot. Please reload this page and try again!";
            } else {
                toastMessage = signupError?.message;
            }

            setAuthStatus("failure");
            toast.error(toastMessage, { id: SIGNUP_TOAST_ID });
            setTimeout(() => setAuthStatus(null), 1000);
        }
    };

    return (
        <AuthMainContainer>
            <AuthLeftContainer style={{ justifyContent: "center", paddingBlock: "0rem" }}>
                <AuthLeftInnerContainer>
                    <AuthKnowtLogo containerStyle={{ marginBottom: "3.2rem" }} onClose={onClose} />
                    <AuthTitle style={{ marginBottom: "1rem" }}>The best all-in-one learning tool</AuthTitle>
                    <AuthDescription style={{ marginBottom: "2rem" }}>
                        Sign up and study or teach for free.
                    </AuthDescription>

                    <AuthSignupSocialBtns event={AuthEvent.GENERAL_SIGNUP} />
                    <GapPerSection />

                    <AuthForm onSubmit={signUpUser}>
                        <OrDivider />
                        <GapPerSection />

                        <>
                            <AuthInputWithLabel
                                label="Email"
                                type="email"
                                value={email}
                                onChange={e => updateProfileForm({ email: e.target.value })}
                                style={{ marginBottom: "2rem" }}
                            />
                            <AuthInputWithLabel
                                label="Password"
                                type="password"
                                value={password}
                                onChange={e => updateProfileForm({ password: e.target.value })}
                            />

                            <GapPerSection height="1rem" />
                            <PasswordRequirementsChecklist password={password} />

                            <GapPerSection height="2rem" />
                            <AuthSubmitButton authStatus={authStatus}>Sign up</AuthSubmitButton>
                        </>
                    </AuthForm>

                    <GapPerSection height="1.2rem" />

                    <ClickableText
                        className={clsx("secondaryText1")}
                        style={{ textAlign: "center" }}
                        onClick={() => openAuthSlide({ showLoginPage: true })}>
                        Already have an account? <span
                        className="secondaryTextBold1"
                        data-testid="auto-id-eea9ba61-9294-44f5-8973-994adcba9c8d"> Log In</span>
                    </ClickableText>

                    <GapPerSection height="2rem" />

                    <span
                        className="secondaryText2"
                        style={{
                            textAlign: "center",
                            maxWidth: "26.8rem",
                            margin: "auto",
                            color: themeColors.neutral2,
                        }}
                        data-testid="auto-id-8d01827b-fbc1-44f9-9714-9cd2fc3bed9e">
                        {"By signing up you agree to our "}
                        <a
                            href="/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "inherit", textDecoration: "underline", fontWeight: "600" }}
                            data-testid="auto-id-dc941177-7522-41d3-a0e7-67a8b829613d">
                            {"Terms of Service"}
                        </a>
                        {" and "}
                        <a
                            href="/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "inherit", textDecoration: "underline", fontWeight: "600" }}
                            data-testid="auto-id-a0cd9f90-aebf-4160-86a0-fd75e8c420aa">
                            {"Privacy Policy"}
                        </a>

                        {/* MOBILE GAP FOR BOTTOM OVERLAY BROWSER TAB */}
                        <div
                            className={br.smUpDisplayNone}
                            style={{ paddingBottom: "5rem" }}
                            data-testid="auto-id-7dd8f8f5-4ed9-4004-9c33-2df0f78ec204" />
                    </span>
                </AuthLeftInnerContainer>
            </AuthLeftContainer>
            <AuthRightContainer onClose={onClose} animation={isDarkMode ? signupAnimationDark : signupAnimationLight} />
        </AuthMainContainer>
    );
};

export default SignupPage;
